import React, { useMemo } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { fromJS } from 'immutable';
import { selectMembers } from 'flow/selectors/TicketsSelectors';
import { selectExtensions } from 'flow/selectors/ExtensionSelectors';
import { sortByName } from 'flow/utils';
import { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import MemberItem from './MemberItem';

interface ExtensionReduced {
    key: ExtensionKey;
}
interface Props {
    ticketKey: TicketKey;
}

const TicketMembers = ({ ticketKey }: Props) => {
    const membersKeys = useAppSelector((state) =>
        selectMembers(state, ticketKey),
    );

    const extensions = useAppSelector(selectExtensions);
    const membersSorted = useMemo(() => {
        if (!membersKeys) return undefined;

        return membersKeys
            .toList()
            .map((key) => {
                const contact = extensions.getIn([key, 'contact']);
                return fromJS({
                    key: key,
                    contact: contact,
                });
            })
            .sort(sortByName);
        /**
         * Reason why I do as I do above instead of simply filtering the extensions is to cover for the case
         * where a ticket member is no longer part of the customer, and thus not present in extensions. In that
         * case, we still want to pass the key to MemberItem (done below, in return), and let that take care of
         * displaying something like 'Unknown contact'. That way we give the user the possibility to
         * remove that member from the ticket.
         */
    }, [extensions, membersKeys]);

    return (
        <ul>
            {membersSorted
                ?.map((extension: TypedMap<ExtensionReduced>) => (
                    <MemberItem
                        key={`tm-mi-${extension.get('key')}`}
                        memberKey={extension.get('key')}
                        ticketKey={ticketKey}
                    />
                ))
                .toArray()}
        </ul>
    );
};

export default TicketMembers;
